import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { useAlert } from '../context/AlertContext';
import { BACKEND_URL } from '../config';
import './Dashboard.css';

const socket = io(BACKEND_URL);

function Dashboard() {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const alert = useAlert();

  useEffect(() => {
    console.log('[Dashboard] Setting up socket listeners');
    
    socket.on('services:status', (status) => {
      console.log('[Dashboard] Received services status:', Object.keys(status));
      setServices(status);
      setLoading(false);
    });

    socket.on('service:error', (data) => {
      alert.error(`${data.service}: ${data.error}`);
    });

    // Request status after a short delay
    const timer = setTimeout(() => {
      console.log('[Dashboard] Requesting services status');
      socket.emit('services:getStatus');
    }, 300);

    // Fallback: stop loading after 3 seconds even if no response
    const fallbackTimer = setTimeout(() => {
      console.log('[Dashboard] Fallback: stopping loading');
      setLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      socket.off('services:status');
      socket.off('service:error');
    };
  }, [alert]);

  const startService = (name) => {
    socket.emit('service:start', name);
    alert.success(`${name} wird gestartet...`);
  };
  
  const stopService = (name) => {
    socket.emit('service:stop', name);
    alert.warning(`${name} wird gestoppt...`);
  };
  
  const restartService = (name) => {
    socket.emit('service:restart', name);
    alert.success(`${name} wird neu gestartet...`);
  };
  
  const startAll = () => {
    socket.emit('services:startAll');
    alert.success('Alle Services werden gestartet...');
  };
  
  const stopAll = () => {
    socket.emit('services:stopAll');
    alert.warning('Alle Services werden gestoppt...');
  };

  const formatUptime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return '#00f593';
      case 'starting': return '#ffb800';
      case 'stopped': return '#eb0400';
      default: return '#adadb8';
    }
  };

  return (
    <div className="dashboard">
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Manage all your streaming services</p>
      </div>

      <div className="control-buttons">
        <button className="btn-success" onClick={startAll}>Start All</button>
        <button className="btn-danger" onClick={stopAll}>Stop All</button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Services werden geladen...</p>
        </div>
      ) : (
        <div className="services-grid">
          {Object.entries(services).map(([name, service]) => (
          <div key={name} className="service-card">
            <div className="service-header">
              <h3>{name.replace('-', ' ').toUpperCase()}</h3>
              <span 
                className="status-indicator" 
                style={{ backgroundColor: getStatusColor(service.status) }}
              />
            </div>
            
            <div className="service-info">
              <p>Status: <strong>{service.status}</strong></p>
              {service.uptime > 0 && (
                <p>Uptime: <strong>{formatUptime(service.uptime)}</strong></p>
              )}
            </div>

            <div className="service-actions">
              <button 
                className="btn-success" 
                onClick={() => startService(name)}
                disabled={service.status === 'running' || service.status === 'starting'}
              >
                Start
              </button>
              <button 
                className="btn-danger" 
                onClick={() => stopService(name)}
                disabled={service.status === 'stopped'}
              >
                Stop
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => restartService(name)}
                disabled={service.status === 'stopped'}
              >
                Restart
              </button>
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
